import { motion } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { Buddy } from '@/store/user';
import { BuddyAvatar, CoachMark, useShowsBuddy } from '@/components/chat/buddy-avatar';
import { columnChild } from '@/components/motion/variants';
import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';

/**
 * The journey's opening panel: who this is, what they are here for, and what
 * they get at the end.
 *
 * It is the navy frosted material rather than a white card — the heavier
 * surface is the structural one, and stacking a light translucent panel on the
 * light translucent module cards would flatten the hierarchy and cost the
 * cards their legibility. The lifesavers read straight through it, which is the
 * point of putting the greeting in a panel at all.
 *
 * The face beside the greeting is at a fixed 56 px box. On the kids path it is
 * the character they chose, bursting out of its badge — the greeting is
 * addressed to them. For teens and adults it is the surf lifesaver (`CoachMark`), the mark
 * they are about to be taught by, because a cartoon character is a
 * primary-school device (ticket 23 §2). The box is fixed either way, so neither
 * a late-decoding avatar nor the swap between them can move the heading.
 *
 * There is no progress count here on purpose. `AppHeader`'s progress slot is
 * the one place that says "n of N" (ticket 16), and a second indicator on the
 * same screen would be two things to reconcile.
 *
 * On a phone this sits above the trail; from `lg` it becomes the sticky left
 * column. Either way the reward reminder lives inside it, which keeps every
 * piece of flow content *above* the trail: the trail is three cards for a
 * teenager and four for everyone else, and when it is the last thing in the
 * flow a change in its length cannot move anything visible.
 */
export function JourneyIntro({
  buddy,
  name,
  total,
  /** Hidden once the course is finished — the all-done card takes over. */
  showReward,
}: {
  buddy: Buddy;
  name: string | undefined;
  total: number;
  showReward: boolean;
}) {
  const { journey } = useStrings();
  const showsBuddy = useShowsBuddy();

  return (
    <motion.section {...columnChild} className="journey-panel rounded-[1.75rem] p-5 text-white sm:p-6">
      <div className="flex items-center gap-3.5">
        {showsBuddy ? (
          <BuddyAvatar buddy={buddy} className="size-14" />
        ) : (
          <CoachMark className="size-14 ring-1 ring-white/20" />
        )}

        <div className="min-w-0">
          <h1
            dir="auto"
            className="font-bebas text-[length:var(--bp-text-step)] leading-[var(--bp-leading-display)] tracking-[var(--bp-tracking-display)] text-balance uppercase"
          >
            {journey.journey_greeting}
            {name ? `, ${name}` : ''}
          </h1>
          {/* The count is the learner's own: a teenager has three modules. */}
          <p dir="auto" className="mt-1 text-sm text-pretty text-white/80">
            {formatString(journey.journey_subtitle, { total })}
          </p>
        </div>
      </div>

      {showReward ? (
        <p dir="auto" className="mt-4 flex items-start gap-2.5 border-t border-white/15 pt-4 text-sm text-white/85">
          <Icon name="passport/passport" aria-hidden className="text-surf-yellow mt-0.5 size-5 shrink-0" />
          {formatString(journey.journey_reward_reminder, { total })}
        </p>
      ) : null}
    </motion.section>
  );
}

/**
 * The all-done state. It replaces the reward reminder rather than joining it:
 * the promise has been kept, so the screen stops promising and starts handing
 * over. Kids see their own character through the "completed" stamp; teens and
 * adults see the same stamp the passport-earned screen celebrates with.
 */
export function JourneyComplete({ theme, buddy }: { theme: string; buddy: Buddy }) {
  const { journey } = useStrings();
  const kids = theme === 'beach';

  return (
    <motion.section
      {...columnChild}
      className="journey-panel rounded-[1.75rem] p-5 text-white sm:p-6"
      // Announced when the last module lands the learner back here.
      aria-live="polite"
    >
      <div className={cn('flex gap-4', kids ? 'items-center' : 'items-start')}>
        {/*
          The stamp on its paper. Every asset is navy artwork on transparency
          — dropped straight onto the navy panel the stamp all but vanishes —
          so it sits on a white disc, which is also what a stamp does in life.
          The kids' version is their character bursting through the stamp, and
          is the card's hero, so it gets the bigger disc. The box is square
          either way, so no asset can move the heading.
        */}
        <span
          className={cn(
            'grid shrink-0 place-items-center rounded-full bg-white/92 ring-1 ring-white/40',
            kids ? 'size-24' : 'size-16',
          )}
        >
          <Media
            {...(kids
              ? { src: `/images/completed-${buddy}.webp`, width: 600, height: 617 }
              : { src: '/images/completed-adult-stamp.webp', width: 305, height: 306 })}
            alt=""
            priority
            placeholder="transparent"
            className={cn('object-contain', kids ? 'size-[5.25rem]' : 'size-12')}
          />
        </span>

        <div className="min-w-0">
          <h2
            dir="auto"
            className="font-bebas text-2xl leading-none tracking-[var(--bp-tracking-display)] text-balance uppercase"
          >
            {journey.journey_all_done_title}
          </h2>
          <p dir="auto" className="mt-1.5 text-sm text-pretty text-white/85">
            {journey.journey_all_done_body}
          </p>
        </div>
      </div>
    </motion.section>
  );
}
